/**
 * Firebase App Check Configuration
 * 
 * Защита Firebase ресурсов от злоупотреблений (ботов, неправомерного использования)
 * 
 * Документация:
 * - https://firebase.google.com/docs/app-check
 * - https://firebase.google.com/docs/app-check/web/recaptcha-v3
 * - https://firebase.google.com/docs/app-check/web/debug-provider
 * 
 * Настройка:
 * 1. В Firebase Console перейдите в App Check
 * 2. Зарегистрируйте ваше приложение с reCAPTCHA v3 или reCAPTCHA Enterprise
 * 3. Получите SITE_KEY и добавьте в VITE_RECAPTCHA_SITE_KEY
 * 4. Для development используйте Debug provider (см. ниже)
 * 
 * Как получить debug token:
 * 1. Запустите приложение в режиме разработки (localhost)
 * 2. Откройте DevTools (F12) → Console
 * 3. Скопируйте debug token из консоли
 * 4. Перейдите в Firebase Console → App Check → Debug Tokens
 * 5. Добавьте токен с названием (например, "localhost-dev")
 * 
 * Важно: NODE_ENV автоматически устанавливается Vite/Vercel
 * Не активировать App Check без реального site key
 */

import { initializeAppCheck, ReCaptchaV3Provider, ReCaptchaEnterpriseProvider, CustomProvider } from 'firebase/app-check';
import type { FirebaseApp } from 'firebase/app';

// Определяем окружение
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const isProduction = process.env.NODE_ENV === 'production';
const isEmulator = import.meta.env.VITE_USE_EMULATORS === 'true';

/**
 * Инициализация App Check
 * 
 * Логика переключения:
 * - Эмулятор: пропускаем инициализацию полностью
 * - Development: используем Debug provider
 * - Production: используем ReCaptchaV3Provider
 * 
 * NODE_ENV устанавливается автоматически:
 * - Vercel: 'production' для production, 'development' для preview/development
 * - Vite: 'development' в dev mode, 'production' в build
 */
export const initAppCheck = (app: FirebaseApp): void => {
  try {
    // Не инициализируем в эмуляторе
    if (isEmulator) {
      console.log('[AppCheck] Emulator mode, skipping initialization');
      return;
    }
    
    // Production: используем ReCaptchaV3Provider
    if (isProduction) {
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      
      if (!siteKey) {
        console.error('[AppCheck] Missing VITE_RECAPTCHA_SITE_KEY in production!');
        return;
      }
      
      console.log('%c🔐 App Check: Production Mode (ReCaptcha v3)', 'color: green; font-weight: bold');
      
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      
      console.log('[AppCheck] Initialized with ReCaptchaV3Provider (production)');
      return;
    }
    
    // Localhost: используем debug provider с явным предупреждением
    if (isLocalhost) {
      console.log('%c🔐 App Check: Localhost Mode (Debug)', 'color: orange; font-weight: bold');
      console.log('%c📋 Для активации App Check в Firebase:', 'color: gray');
      console.log('%c1. Скопируйте debug token из консоли после запуска', 'color: gray');
      console.log('%c2. Firebase Console → App Check → Debug Tokens → Add token', 'color: gray');
      
      const debugToken = import.meta.env.VITE_APP_CHECK_DEBUG_TOKEN || '';
      
      initializeAppCheck(app, {
        provider: new CustomProvider({
          getToken: async () => {
            return {
              token: debugToken || 'debug-token-placeholder',
              expireTimeMillis: Date.now() + 3600000,
            };
          },
        }),
        isTokenAutoRefreshEnabled: false,
      });
      
      console.log('[AppCheck] Initialized with Debug provider (localhost)');
      return;
    }
    
    // Development: используем Debug provider
    // Выводим инструкцию для получения debug token
    console.log('%c🔐 App Check: Development Mode', 'color: orange; font-weight: bold');
    console.log('%c📋 Инструкция для получения debug token:', 'color: gray');
    console.log('%c1. Скопируйте debug token из консоли браузера', 'color: gray');
    console.log('%c2. Перейдите: Firebase Console → App Check → Debug Tokens', 'color: gray');
    console.log('%c3. Добавьте токен с названием (например, "localhost-dev")', 'color: gray');
    console.log('%c4. Установите VITE_APP_CHECK_DEBUG_TOKEN в .env.development', 'color: gray');
    
    const debugToken = import.meta.env.VITE_APP_CHECK_DEBUG_TOKEN || '';
    
    if (!debugToken) {
      console.warn(
        '%c[AppCheck] ВНИМАНИЕ: VITE_APP_CHECK_DEBUG_TOKEN не установлен!\n' +
        'Без debug token Firebase может блокировать запросы.',
        'color: yellow; font-weight: bold'
      );
    } else {
      console.log('%c[AppCheck] Debug token настроен: ' + debugToken.substring(0, 10) + '...', 'color: green');
    }
    
    initializeAppCheck(app, {
      provider: new CustomProvider({
        getToken: async () => {
          return {
            token: debugToken || 'debug-token-placeholder',
            expireTimeMillis: Date.now() + 3600000, // 1 час
          };
        },
      }),
      isTokenAutoRefreshEnabled: true,
    });
    
    console.log('[AppCheck] Initialized with Debug provider (development mode)');
  } catch (error) {
    console.error('[AppCheck] Failed to initialize:', error);
  }
};

/**
 * Инициализация с ReCaptchaEnterpriseProvider (для более продвинутой защиты)
 * 
 * Требует настройки reCAPTCHA Enterprise в Firebase Console
 */
export const initAppCheckEnterprise = (app: FirebaseApp): void => {
  try {
    const siteKey = import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY || '';
    
    if (!siteKey) {
      console.warn(
        '[AppCheck] VITE_RECAPTCHA_ENTERPRISE_SITE_KEY not configured. ' +
        'Using ReCaptchaV3Provider instead.'
      );
      initAppCheck(app);
      return;
    }
    
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    
    console.log('[AppCheck] Initialized with ReCaptchaEnterpriseProvider');
  } catch (error) {
    console.error('[AppCheck] Failed to initialize with Enterprise provider:', error);
  }
};

/**
 * Получение debug токена для локальной разработки
 * 
 * При первом запуске с Debug provider в консоли браузера появится токен,
 * который нужно зарегистрировать в Firebase Console > App Check
 * 
 * @returns void
 */
export const getDebugToken = (): void => {
  // Этот метод вызывается автоматически при инициализации
  // Токен будет выведен в консоль браузера
  console.log(
    '[AppCheck] Debug mode enabled. ' +
    'Copy the debug token from browser console and add it in Firebase Console > App Check'
  );
};

// Экспорт провайдеров для кастомной конфигурации
export { ReCaptchaV3Provider, ReCaptchaEnterpriseProvider, CustomProvider };
